"use client";

/**
 * WorkspaceMenuSection — the switchable workspace list, designed to sit INSIDE
 * the top-right account dropdown (org switcher folded into the user menu).
 *
 * Renders nothing when the user has ≤ 1 workspace (nothing to switch, and it
 * would just echo the account menu). Otherwise: a "Workspaces" label + one row
 * per org with active-check, role badge, and a default-★ toggle.
 *
 * All switching logic (memberships, setActiveOrg, default star) lives here so
 * the layout only has to drop <WorkspaceMenuSection/> into its menu.
 */

import { useAuth } from "@/lib/auth";

/** Single capital letter initial for a workspace. */
function orgInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "W";
}

/** Small role badge. */
function RoleBadge({ role }: { role: string | null | undefined }) {
  if (!role) return null;
  const label = role.charAt(0).toUpperCase() + role.slice(1);
  return (
    <span className="inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wider shrink-0 bg-slate-100 text-slate-600">
      {label}
    </span>
  );
}

export function WorkspaceMenuSection({ onNavigate }: { onNavigate?: () => void }) {
  const { activeOrg, memberships, setActiveOrg, user, setDefaultWorkspace } = useAuth();

  // Single (or zero) workspace → nothing to switch; don't render the section.
  if (memberships.length <= 1) return null;

  const displayOrg = activeOrg ?? memberships[0];
  const defaultSlug = user?.default_org_slug ?? null;

  return (
    <div className="border-b border-slate-100 py-1.5">
      <p className="px-4 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
        Workspaces
      </p>
      {memberships.map((org) => {
        const isActive = org.slug === displayOrg?.slug;
        const isDefault = org.slug === defaultSlug;
        return (
          // Flex wrapper carries the row bg; the star is a sibling of the row
          // button so no <button> is nested inside another <button>.
          <div
            key={org.id}
            className={`flex items-center transition-colors ${
              isActive ? "bg-brand-50" : "hover:bg-slate-50"
            }`}
          >
            <button
              role="option"
              aria-selected={isActive}
              onClick={() => {
                onNavigate?.();
                if (!isActive) setActiveOrg(org.slug);
              }}
              className="flex flex-1 min-w-0 items-center gap-2.5 pl-4 pr-1 py-1.5 text-left"
            >
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-bold font-display shrink-0 ${
                  isActive ? "bg-brand-200 text-brand-700" : "bg-slate-100 text-slate-600"
                }`}
              >
                {orgInitial(org.name)}
              </div>
              <span
                className={`flex-1 min-w-0 truncate text-[13px] font-medium ${
                  isActive ? "text-brand-700" : "text-slate-700"
                }`}
              >
                {org.name}
              </span>
              <RoleBadge role={org.role} />
              {isActive && (
                <svg
                  className="h-3.5 w-3.5 text-brand-600 shrink-0 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              )}
            </button>

            <button
              type="button"
              title={isDefault ? "Default — you land here on login" : "Set as default workspace"}
              aria-label={isDefault ? "Default workspace" : "Set as default workspace"}
              className="flex items-center self-stretch shrink-0 pl-1 pr-3"
              onClick={(e) => {
                e.stopPropagation();
                void setDefaultWorkspace(isDefault ? null : org.slug);
              }}
            >
              <span className={isDefault ? "text-brand-500" : "text-slate-300 hover:text-brand-500"}>
                {isDefault ? "★" : "☆"}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
